// src/components/MobileBottomNav.jsx
//
// Fixed bottom-of-viewport quick-action bar for phones. Hidden on md+
// (Layout keeps rendering the drawer there). Exposes up to 5 primary
// destinations: Dashboard, Alarms, Tickets, Locations, Notifications.
// Permission-gated (admin-only Alarms, permission-gated Tickets) —
// items the user cannot access are simply omitted.
//
// Behavior:
//   - Active route highlighted via `location.pathname.startsWith(path)`.
//   - Notification slot shows an unread badge (uses notificationService).
//   - Respects iOS safe-area-inset-bottom (home indicator).
//   - Height is reported via the `BOTTOM_NAV_HEIGHT` export so Layout can
//     pad the main content and the notifications drawer can clear it.

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Lightbulb as LightbulbIcon,
  LocalHospital as AlarmIcon,
  BugReport as TicketsIcon,
  LocationOn as LocationsIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import notificationService from '../services/notificationService';
import useVisibilityPoll from '../hooks/useVisibilityPoll';

export const BOTTOM_NAV_HEIGHT = 56;

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const [unread, setUnread] = useState(0);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      // notificationService.getUnreadCount() resolves to a plain number
      // (see NotificationCenter usage). Guard against future shape drift.
      const res = await notificationService.getUnreadCount();
      const n = typeof res === 'number' ? res : (res?.count ?? res?.unreadCount ?? 0);
      setUnread(n);
    } catch {
      /* ignore — bell in the app bar handles its own errors */
    }
  }, [isAuthenticated, user]);

  // Poll every 30s while authenticated and the tab is visible.
  useVisibilityPoll(load, 30000, !!(isAuthenticated && user));

  const isAdmin = user?.role === 'admin';
  // Was `hasPermission('tickets.view') || true` — the `|| true` made the check
  // decorative, so revoking tickets.view left the item in the bar. Now it behaves
  // exactly like canSeeIdeas below, which already had it right.
  const canSeeTickets = hasPermission ? hasPermission('tickets.view') : false;
  // Portal Ideabox (spec 20260817-portal-ideabox §8). Gated on the real permission —
  // every portal role holds `ideas.view` today, but an admin who revokes it should
  // see the item disappear.
  const canSeeIdeas = hasPermission ? hasPermission('ideas.view') : false;

  // Build the list, filter invisibles. Keeps 3-5 slots ideally.
  const items = [
    { value: '/dashboard', label: 'Home', icon: <DashboardIcon />, show: true },
    { value: '/alarms', label: 'Alarms', icon: <AlarmIcon />, show: isAdmin },
    { value: '/tickets', label: 'Tickets', icon: <TicketsIcon />, show: canSeeTickets },
    { value: '/locations', label: 'Locations', icon: <LocationsIcon />, show: true },
    {
      value: '/notifications',
      label: 'Alerts',
      icon: (
        <Badge badgeContent={unread} color="error" max={99} overlap="circular">
          <NotificationsIcon />
        </Badge>
      ),
      show: true,
    },
    // ⚠ SLOT BUDGET. The comment above targets 3-5 slots; with Ideas visible this
    // bar reaches 6, which MUI BottomNavigation renders but crowds on a 360px
    // viewport. Ideas is placed LAST deliberately — it is the lowest-frequency
    // surface here, so it is the one that should degrade first. If a 7th item is
    // ever added, this bar needs an overflow affordance rather than another entry.
    { value: '/ideas', label: 'Ideas', icon: <LightbulbIcon />, show: canSeeIdeas },
  ].filter((x) => x.show);

  const matchedValue = items.find((it) => location.pathname.startsWith(it.value))?.value || false;

  return (
    <Paper
      elevation={6}
      square
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: { xs: 'block', md: 'none' },
        // iOS safe area — pad the bar itself so the actions don't sit
        // on top of the home indicator.
        pb: 'env(safe-area-inset-bottom, 0px)',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <BottomNavigation
        showLabels
        value={matchedValue}
        onChange={(_e, v) => {
          if (v && v !== matchedValue) navigate(v);
        }}
        sx={{
          height: BOTTOM_NAV_HEIGHT,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 56,
            padding: '6px 8px 8px',
            transition: 'color 180ms ease, transform 180ms ease',
          },
          '& .MuiBottomNavigationAction-root.Mui-selected': {
            // Subtle scale-up on selection gives a native-app tap feel.
            transform: 'translateY(-1px) scale(1.03)',
          },
          '& .MuiBottomNavigationAction-root .MuiSvgIcon-root': {
            transition: 'transform 180ms ease',
          },
          '& .MuiBottomNavigationAction-root.Mui-selected .MuiSvgIcon-root': {
            transform: 'scale(1.06)',
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.72rem',
            transition: 'font-size 180ms ease, opacity 180ms ease',
            '&.Mui-selected': { fontSize: '0.75rem', fontWeight: 600 },
          },
        }}
      >
        {items.map((it) => (
          <BottomNavigationAction
            key={it.value}
            value={it.value}
            label={it.label}
            icon={it.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
